import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { createClient } from '@/lib/supabase/client';
import { CheckCircle, XCircle, ArrowLeft, Plus, FileText, Layers, UploadCloud, Info, Settings, HelpCircle, Eye, EyeOff, Pencil, Trash } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

function Tooltip({ text }: { text: string }) {
  return (
    <span className="ml-1 text-xs text-muted-foreground cursor-help" title={text}>
      <HelpCircle className="inline w-4 h-4 align-text-bottom" />
    </span>
  );
}

export default function EditarCursoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const supabase = createClient();
  const [curso, setCurso] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'info' | 'aulas' | 'materiais' | 'config'>('info');
  const [saving, setSaving] = useState(false);
  // Checklist de progresso
  const [aulas, setAulas] = useState<any[]>([]);
  const [materiais, setMateriais] = useState<any[]>([]);

  // Edição inline
  const [editForm, setEditForm] = useState({ title: '', description: '', image_url: '', status: 'active' });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchCurso = async () => {
      setLoading(true);
      const { data } = await supabase.from('courses').select('*').eq('id', id).single();
      setCurso(data);
      setEditForm({
        title: data?.title || '',
        description: data?.description || '',
        image_url: data?.image_url || '',
        status: data?.status || 'active',
      });
      setLoading(false);
    };
    fetchCurso();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    // Buscar aulas e materiais
    const fetchAulas = async () => {
      const { data } = await supabase.from('lessons').select('*').eq('course_id', id);
      setAulas(data || []);
    };
    const fetchMateriais = async () => {
      const { data } = await supabase.from('materials').select('*').eq('course_id', id);
      setMateriais(data || []);
    };
    fetchAulas();
    fetchMateriais();
  }, [id]);

  // Checklist lateral
  const checklist = [
    { label: 'Informações', key: 'info', done: !!curso?.title && !!curso?.description && !!curso?.image_url, icon: <Info className="w-5 h-5" /> },
    { label: 'Aulas', key: 'aulas', done: aulas.length > 0, icon: <Layers className="w-5 h-5" /> },
    { label: 'Materiais', key: 'materiais', done: materiais.length > 0, icon: <UploadCloud className="w-5 h-5" /> },
    { label: 'Configurações', key: 'config', done: true, icon: <Settings className="w-5 h-5" /> },
  ];
  const progresso = Math.round((checklist.filter(c => c.done).length / checklist.length) * 100);
  const allDone = checklist.every(c => c.done);

  // Edição inline handlers
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      setSaving(true);
      const { error: uploadError } = await supabase.storage.from('course-images').upload(filePath, file, { upsert: false });
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from('course-images').getPublicUrl(filePath);
        setEditForm(f => ({ ...f, image_url: urlData.publicUrl }));
        toast.success('Imagem atualizada!');
      } else {
        toast.error('Erro ao fazer upload da imagem.');
      }
      setSaving(false);
    }
  };
  const handleSaveInfo = async () => {
    setSaving(true);
    const { error } = await supabase.from('courses').update(editForm).eq('id', id);
    if (!error) {
      toast.success('Informações salvas!');
      setCurso((c: any) => ({ ...c, ...editForm }));
    } else {
      toast.error('Erro ao salvar informações.');
    }
    setSaving(false);
  };
  const handleToggleStatus = async () => {
    setSaving(true);
    const newStatus = editForm.status === 'active' ? 'inactive' : 'active';
    const { error } = await supabase.from('courses').update({ status: newStatus }).eq('id', id);
    if (!error) {
      setEditForm(f => ({ ...f, status: newStatus }));
      setCurso((c: any) => ({ ...c, status: newStatus }));
      toast.success(newStatus === 'active' ? 'Curso publicado!' : 'Curso despublicado!');
    } else {
      toast.error('Erro ao atualizar status.');
    }
    setSaving(false);
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <Button variant="ghost" onClick={() => navigate('/dashboard/admin/cursos')} className="mb-6 flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Voltar para cursos
      </Button>
      <div className="flex flex-col md:flex-row gap-8">
        {/* Checklist lateral responsivo */}
        <nav className="mb-6 md:mb-0 w-full md:w-64 flex-shrink-0">
          <div className="bg-card rounded-xl shadow p-4 sticky top-8 hidden md:block">
            <h2 className="text-lg font-semibold mb-4">Progresso do curso</h2>
            <div className="w-full flex flex-col items-center mb-2">
              <span className="text-xs text-muted-foreground font-semibold">{progresso}%</span>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-1">
                <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${progresso}%` }} />
              </div>
            </div>
            <ul className="space-y-2">
              {checklist.map((item) => (
                <li key={item.key}>
                  <button
                    className={`flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg transition-colors ${tab === item.key ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted text-muted-foreground'}`}
                    onClick={() => setTab(item.key as any)}
                  >
                    <span>{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {item.done ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </li>
              ))}
            </ul>
            <Button
              className="w-full mt-6"
              variant={allDone && editForm.status !== 'active' ? 'default' : 'outline'}
              disabled={!allDone || saving || editForm.status === 'active'}
              onClick={handleToggleStatus}
            >
              <Eye className="w-4 h-4 mr-1" /> Publicar curso
            </Button>
            {editForm.status === 'active' && (
              <Button
                className="w-full mt-2"
                variant="outline"
                disabled={saving}
                onClick={handleToggleStatus}
              >
                <EyeOff className="w-4 h-4 mr-1" /> Despublicar curso
              </Button>
            )}
          </div>
          {/* Mobile: checklist horizontal */}
          <div className="md:hidden mb-4 flex flex-col w-full min-w-0">
            <div className="flex flex-col items-center w-full min-w-0 mt-2">
              <span className="text-xs text-muted-foreground font-semibold">{progresso}%</span>
              <div className="w-full min-w-0 h-2 bg-muted rounded-full overflow-hidden mt-1">
                <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${progresso}%` }} />
              </div>
            </div>
          </div>
        </nav>
        {/* Conteúdo principal */}
        <main className="flex-1">
          {loading || !curso ? (
            <Skeleton className="h-64 w-full rounded-xl mb-6" />
          ) : (
            <div className="bg-card rounded-xl shadow p-6 mb-6 flex flex-col md:flex-row gap-6 items-center md:items-start">
              <img
                src={editForm.image_url || '/placeholder.svg'}
                alt="Capa do curso"
                className="w-40 h-40 object-cover rounded-lg border shadow-md bg-muted cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                title="Clique para alterar a imagem"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              <div className="flex-1 flex flex-col gap-2">
                <input
                  className="text-3xl font-bold bg-transparent border-none outline-none focus:ring-2 focus:ring-primary/30 rounded px-1"
                  name="title"
                  value={editForm.title}
                  onChange={handleEditChange}
                  placeholder="Nome do curso"
                  maxLength={80}
                  autoComplete="off"
                />
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${editForm.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{editForm.status === 'active' ? 'Ativo' : 'Rascunho'}</span>
                <textarea
                  className="text-muted-foreground mt-2 bg-transparent border border-muted rounded px-2 py-1 focus:ring-2 focus:ring-primary/30 outline-none"
                  name="description"
                  value={editForm.description}
                  onChange={handleEditChange}
                  placeholder="Descrição do curso"
                  rows={3}
                  maxLength={500}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" onClick={handleSaveInfo} disabled={saving} variant="default">
                    Salvar informações
                  </Button>
                  <Tooltip text="Edite as informações e clique em salvar para atualizar." />
                </div>
              </div>
            </div>
          )}
          {/* Remover tabs horizontais e mostrar apenas o conteúdo da etapa selecionada */}
          <div>
            {tab === 'info' && (
              <div>
                <h3 className="text-lg font-semibold mb-2">Informações do curso</h3>
                <p className="text-muted-foreground mb-4">Edite as informações básicas do curso, imagem, nome e descrição. As alterações são salvas individualmente.</p>
              </div>
            )}
            {tab === 'aulas' && (
              <AulasSection courseId={id as string} />
            )}
            {tab === 'materiais' && (
              <MateriaisSection courseId={id as string} />
            )}
            {tab === 'config' && (
              <div>
                <h3 className="text-lg font-semibold mb-2">Configurações</h3>
                <p className="text-muted-foreground mb-4">Configurações avançadas do curso (em breve).</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function AulasSection({ courseId }: { courseId: string }) {
  const [aulas, setAulas] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    video_url: '',
    order: 1,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAulas = async () => {
      setLoading(true);
      const { data } = await supabase.from('lessons').select('*').eq('course_id', courseId).order('order');
      setAulas(data || []);
      setLoading(false);
    };
    fetchAulas();
  }, [courseId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      course_id: courseId,
      order: Number(form.order) || 1,
    };
    if (editId) {
      await supabase.from('lessons').update(payload).eq('id', editId);
    } else {
      await supabase.from('lessons').insert(payload);
    }
    setOpen(false);
    setForm({ title: '', description: '', video_url: '', order: 1 });
    setEditId(null);
    setLoading(false);
    // Recarregar lista
    const { data } = await supabase.from('lessons').select('*').eq('course_id', courseId).order('order');
    setAulas(data || []);
  };

  const handleEdit = (aula: any) => {
    setForm({
      title: aula.title || '',
      description: aula.description || '',
      video_url: aula.video_url || '',
      order: aula.order || 1,
    });
    setEditId(aula.id);
    setOpen(true);
  };

  const handleDelete = async (aulaId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta aula?')) return;
    setLoading(true);
    await supabase.from('lessons').delete().eq('id', aulaId);
    setAulas((prev) => prev.filter((a) => a.id !== aulaId));
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ title: '', description: '', video_url: '', order: 1 });
  };

  return (
    <div className="max-w-3xl mx-auto py-4 px-0">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Aulas do Curso</h3>
        <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : handleClose()}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditId(null); setForm({ title: '', description: '', video_url: '', order: aulas.length + 1 }); }}>
              <Plus className="mr-2 h-4 w-4" /> Nova Aula
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editId ? 'Editar Aula' : 'Nova Aula'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Título</label>
                <Input
                  name="title"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição</label>
                <Input
                  name="description"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vídeo (URL)</label>
                <Input
                  name="video_url"
                  value={form.video_url}
                  onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordem</label>
                <Input
                  name="order"
                  type="number"
                  min={1}
                  value={form.order}
                  onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>Cancelar</Button>
                <Button type="submit" disabled={loading}>{editId ? 'Salvar' : 'Criar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-4">
        {aulas.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">Nenhuma aula cadastrada ainda.</div>
        ) : (
          aulas.map((aula) => (
            <div key={aula.id} className="rounded-lg border p-4 flex flex-col md:flex-row md:items-center gap-4 bg-white shadow-sm">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg">{aula.title}</span>
                  <span className="text-xs text-muted-foreground">Ordem: {aula.order}</span>
                </div>
                <div className="text-sm text-muted-foreground mb-1">{aula.description}</div>
                {aula.video_url && (
                  <a href={aula.video_url} target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">Ver vídeo</a>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(aula)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(aula.id)}><Trash className="w-4 h-4" /></Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MateriaisSection({ courseId }: { courseId: string }) {
  const [materiais, setMateriais] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    file_url: '',
    order: 1,
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchMateriais = async () => {
      setLoading(true);
      const { data } = await supabase.from('materials').select('*').eq('course_id', courseId).order('order');
      setMateriais(data || []);
      setLoading(false);
    };
    fetchMateriais();
  }, [courseId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      course_id: courseId,
      order: Number(form.order) || 1,
    };
    if (editId) {
      await supabase.from('materials').update(payload).eq('id', editId);
    } else {
      await supabase.from('materials').insert(payload);
    }
    setOpen(false);
    setForm({ title: '', description: '', file_url: '', order: 1 });
    setEditId(null);
    setLoading(false);
    // Recarregar lista
    const { data } = await supabase.from('materials').select('*').eq('course_id', courseId).order('order');
    setMateriais(data || []);
  };

  const handleEdit = (material: any) => {
    setForm({
      title: material.title || '',
      description: material.description || '',
      file_url: material.file_url || '',
      order: material.order || 1,
    });
    setEditId(material.id);
    setOpen(true);
  };

  const handleDelete = async (materialId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este material?')) return;
    setLoading(true);
    await supabase.from('materials').delete().eq('id', materialId);
    setMateriais((prev) => prev.filter((m) => m.id !== materialId));
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ title: '', description: '', file_url: '', order: 1 });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop();
      const filePath = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      setLoading(true);
      const { error: uploadError } = await supabase.storage.from('course-materiais').upload(filePath, file, { upsert: false });
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from('course-materiais').getPublicUrl(filePath);
        setForm(f => ({ ...f, file_url: urlData.publicUrl }));
        toast.success('Arquivo enviado!');
      } else {
        toast.error('Erro ao fazer upload do arquivo.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-4 px-0">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Materiais do Curso</h3>
        <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : handleClose()}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditId(null); setForm({ title: '', description: '', file_url: '', order: materiais.length + 1 }); }}>
              <Plus className="mr-2 h-4 w-4" /> Novo Material
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editId ? 'Editar Material' : 'Novo Material'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Título</label>
                <Input
                  name="title"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição</label>
                <Input
                  name="description"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Arquivo <span className="text-red-500">*</span></label>
                <div className="flex gap-2 items-center">
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                    Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {form.file_url && (
                    <a href={form.file_url} target="_blank" rel="noopener noreferrer" className="text-primary underline text-xs mt-1 block">
                      {form.file_url.split('/').pop()}
                    </a>
                  )}
                </div>
                {!form.file_url && <span className="text-xs text-red-500">É necessário fazer upload de um arquivo.</span>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ordem</label>
                <Input
                  name="order"
                  type="number"
                  min={1}
                  value={form.order}
                  onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose}>Cancelar</Button>
                <Button type="submit" disabled={loading || !form.file_url}>{editId ? 'Salvar' : 'Criar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-4">
        {materiais.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">Nenhum material cadastrado ainda.</div>
        ) : (
          materiais.map((material) => (
            <div key={material.id} className="rounded-lg border p-4 flex flex-col md:flex-row md:items-center gap-4 bg-white shadow-sm">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg">{material.title}</span>
                  <span className="text-xs text-muted-foreground">Ordem: {material.order}</span>
                </div>
                <div className="text-sm text-muted-foreground mb-1">{material.description}</div>
                {material.file_url && (
                  <a href={material.file_url} target="_blank" rel="noopener noreferrer" className="text-primary underline text-sm">Ver arquivo</a>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(material)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(material.id)}><Trash className="w-4 h-4" /></Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 